import React from "react";
import { Box, Paper, Typography, Alert, Collapse, Button } from "@mui/material";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
  FaArrowUp,
  FaUserCheck,
  FaComments,
  FaBan,
  FaClock,
} from "react-icons/fa";
import { useState } from "react";

const QualityRatingsMessage = ({ qualityRating, showDetails = true }) => {
  const [expanded, setExpanded] = useState(false);

  const ratingConfig = {
    GREEN: {
      icon: FaCheckCircle,
      color: "#4caf50",
      bgColor: "rgba(76, 175, 80, 0.1)",
      borderColor: "#4caf50",
      title: "Excellent Quality Rating",
      status: "Good to go!",
      message:
        "Your account is in good standing. You can proceed with campaigns.",
      type: "success",
    },
    YELLOW: {
      icon: FaExclamationTriangle,
      color: "#ff9800",
      bgColor: "rgba(255, 152, 0, 0.1)",
      borderColor: "#ff9800",
      title: "Warning - Quality Issues",
      status: "Proceed with caution",
      message: "Your messages may experience delivery issues.",
      type: "warning",
    },
    RED: {
      icon: FaTimesCircle,
      color: "#f44336",
      bgColor: "rgba(244, 67, 54, 0.1)",
      borderColor: "#f44336",
      title: "Critical Quality Issues",
      status: "Campaigns not recommended",
      message: "High risk of message failures or account restrictions.",
      type: "error",
    },
  };

  const config = ratingConfig[qualityRating] || {
    icon: FaInfoCircle,
    color: "#9e9e9e",
    bgColor: "rgba(158, 158, 158, 0.1)",
    title: "No Rating Available",
    status: "Unknown",
    message: "Quality rating not available.",
    type: "info",
  };

  const IconComponent = config.icon;

  const improvementTips = [
    {
      icon: FaUserCheck,
      title: "Maintain High Engagement",
      description:
        "Ensure recipients want to receive your messages. High opt-in rates and positive interactions improve quality.",
    },
    {
      icon: FaBan,
      title: "Avoid Spam-like Behavior",
      description:
        "Don't send unsolicited messages. Respect opt-outs immediately and avoid aggressive messaging.",
    },
    {
      icon: FaComments,
      title: "Quality Message Content",
      description:
        "Send relevant, valuable content. Avoid misleading information, spam keywords, or poor formatting.",
    },
    {
      icon: FaClock,
      title: "Respect Timing & Frequency",
      description:
        "Don't bombard users. Space messages appropriately and send during reasonable hours.",
    },
    {
      icon: FaArrowUp,
      title: "Monitor Metrics",
      description:
        "Regularly check delivery rates, engagement metrics, and user feedback to identify issues early.",
    },
  ];

  const shouldShowWarning =
    qualityRating === "YELLOW" || qualityRating === "RED";

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 2,
        border: `2px solid ${config.borderColor}`,
        borderRadius: 2,
        overflow: "hidden",
        mb: 2,
      }}
    >
      {/* Rating Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: 2,
          backgroundColor: config.bgColor,
          cursor: showDetails ? "pointer" : "default",
        }}
        onClick={() => showDetails && setExpanded(!expanded)}
      >
        <IconComponent
          style={{
            fontSize: "2rem",
            color: config.color,
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
          }}
        />

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold" color={config.color}>
            {config.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {config.status}
          </Typography>
        </Box>

        {showDetails && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {expanded ? "Click to collapse" : "Click for details"}
          </Typography>
        )}
      </Box>

      {/* Warning Alert for Yellow/Red */}
      {shouldShowWarning && (
        <Alert
          severity={config.type}
          sx={{
            borderRadius: 0,
            borderBottom: `1px solid ${config.borderColor}`,
          }}
        >
          <Typography fontWeight="bold">
            ⚠️ Campaign Recommendation:
            <Box component="span" sx={{ color: config.color, ml: 1 }}>
              {qualityRating === "RED" ? "DO NOT SEND" : "RECONSIDER SENDING"}
            </Box>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {qualityRating === "RED"
              ? "Sending campaigns now may result in message failures, high costs, or account blocking. Address quality issues first."
              : "Messages may experience delivery delays or higher costs. Improve quality before large campaigns."}
          </Typography>
        </Alert>
      )}

      {/* Expandable Details */}
      <Collapse in={expanded && showDetails}>
        <Box sx={{ p: 3 }}>
          {/* Current Status Message */}
          <Typography variant="body1" paragraph>
            {config.message}
          </Typography>

          {/* Quality Improvement Crash Course */}
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <FaInfoCircle color={config.color} />
              How to Improve & Maintain Green Status
            </Typography>

            <Box sx={{ display: "grid", gap: 2, mt: 2 }}>
              {improvementTips.map((tip, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <tip.icon
                      style={{
                        color: config.color,
                        fontSize: "1.2rem",
                        marginTop: "2px",
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {tip.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tip.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Quick Action Tips */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Quick Actions to Improve Rating:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                <li>
                  <Typography variant="body2">
                    Review recent message templates for compliance
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Check opt-out rates and user complaints
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Reduce message frequency if too high
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Ensure all messages provide clear value
                  </Typography>
                </li>
              </Box>
            </Alert>

            {/* Recovery Timeline */}
            {shouldShowWarning && (
              <Paper
                variant="outlined"
                sx={{ p: 2, mt: 2, bgcolor: "warning.light" }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  Recovery Timeline:
                </Typography>
                <Typography variant="body2">
                  {qualityRating === "YELLOW"
                    ? "With consistent good behavior, yellow ratings typically improve within 7-14 days."
                    : "Red ratings require significant improvement and may take 14-30 days to recover with perfect sending behavior."}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};
export default QualityRatingsMessage;
